'use client';
import { jsx as _jsx } from "react/jsx-runtime";
import { ComputerCommandMenuDetail, ComputerCommandMenuDetailSkeleton, } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/ComputerCommandMenuDetail.js';
import { ComputerCommandMenuSkillDetailsAction } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/ComputerCommandMenuSkillDetailsAction.js';
import { PluginCommandMenuDescription } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/plugins/PluginCommandMenuItems.js';
import { toTypeaheadGroups } from '#pplx/frontend/apps/ask/composer-sdk-plugins/skills-and-commands/computerMenuTypeaheadItems.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { createPlugin } from '#pplx/frontend/composer-sdk/plugins/createPlugin.js';
import { useMemo } from 'react';
import { defineMessage, defineMessages, useIntl } from 'react-intl';
const COMMAND_QUERY_PLACEHOLDER = defineMessage({
    defaultMessage: 'Type to search',
    description: 'Inline hint shown after typing slash in the Computer command menu',
    id: '9869fd5d59',
});
const SECTION_TITLES = defineMessages({
    command: {
        defaultMessage: 'Commands',
        description: 'Section label for menu commands in the Computer command menu',
        id: 'b24dec5351',
    },
    plugin: {
        defaultMessage: 'Plugins',
        description: 'Section label for installed plugins in the Computer command menu',
        id: '277e5fff34',
    },
    skill: {
        defaultMessage: 'Skills',
        description: 'Section label for user skills in the Computer command menu',
        id: 'a20add8367',
    },
});
/** Adapts shared Computer menu rows and selection to the Composer menu shell. */
export function useSkillsAndCommandsPlugin({ computerCommandMenuOpen, onComputerCommandMenuOpenChange, showComputerCommandMenu, getMenuData, onItemSelect, onSkillDetailsOpen, getUseButtonTrack, }) {
    const intl = useIntl();
    const placeholder = intl.formatMessage(COMMAND_QUERY_PLACEHOLDER);
    const typeahead = useMemo(() => {
        if (computerCommandMenuOpen === undefined || onComputerCommandMenuOpenChange === undefined) {
            return undefined;
        }
        return {
            commandMenu: {
                open: showComputerCommandMenu && computerCommandMenuOpen,
                onOpenChange: onComputerCommandMenuOpenChange,
                placeholder,
                placeholderVisibility: 'emptyEditor',
            },
        };
    }, [
        computerCommandMenuOpen,
        onComputerCommandMenuOpenChange,
        placeholder,
        showComputerCommandMenu,
    ]);
    const commandPlugin = useMemo(() => createPlugin({
        id: 'skills-and-commands',
        capabilities: [
            {
                kind: 'mention',
                configure: () => ({ renderIcon: () => undefined }),
            },
            {
                kind: 'typeahead',
                providers: [
                    {
                        trigger: '/',
                        display: 'commandMenu',
                        navigation: 'clamp',
                        matchQuery: () => showComputerCommandMenu,
                        getResults: (query) => {
                            const { presentation, resolvedSearchQuery, emptyMessage, pagination } = getMenuData(query);
                            return {
                                groups: toTypeaheadGroups({
                                    ...presentation,
                                    resolvedSearchQuery,
                                    sectionTitles: {
                                        command: intl.formatMessage(SECTION_TITLES.command),
                                        plugin: intl.formatMessage(SECTION_TITLES.plugin),
                                        skill: intl.formatMessage(SECTION_TITLES.skill),
                                    },
                                }),
                                status: presentation.status,
                                emptyMessage,
                                ...(pagination?.hasNextPage
                                    ? {
                                        continuation: {
                                            isPending: pagination.isFetchingNextPage,
                                            request: (reason) => {
                                                switch (reason) {
                                                    case 'button':
                                                        pagination.onLoadMoreClick();
                                                        return;
                                                    case 'scroll':
                                                        pagination.onFetchNextPage();
                                                        return;
                                                    default:
                                                        return assertUnreachableValue(reason);
                                                }
                                            },
                                        },
                                    }
                                    : {}),
                            };
                        },
                        renderSidePanel: (state) => {
                            switch (state.status) {
                                case 'loading':
                                    return _jsx(ComputerCommandMenuDetailSkeleton, {});
                                case 'ready': {
                                    const item = state.item.payload;
                                    return (_jsx(ComputerCommandMenuDetail, { item: item, additionalDescription: _jsx(PluginCommandMenuDescription, { item: item }), actions: _jsx(ComputerCommandMenuSkillDetailsAction, { item: item, onSkillDetailsOpen: onSkillDetailsOpen }), onSelect: state.select, useButtonTrack: getUseButtonTrack?.(item) ?? false }));
                                }
                                default:
                                    return assertUnreachableValue(state);
                            }
                        },
                        // Registration erases the provider's subtype; every row is mapped above.
                        onSelect: (item, editor, context) => {
                            const payload = item.payload;
                            if (context.source === 'sidePanel') {
                                return onItemSelect(payload, 'button', editor);
                            }
                            switch (context.selectionMethod) {
                                case 'pointer':
                                    return onItemSelect(payload, 'row', editor);
                                case 'keyboard':
                                    return onItemSelect(payload, 'keyboard', editor);
                                default:
                                    return assertUnreachableValue(context.selectionMethod);
                            }
                        },
                    },
                ],
            },
        ],
    }), [
        getMenuData,
        getUseButtonTrack,
        intl,
        onItemSelect,
        onSkillDetailsOpen,
        showComputerCommandMenu,
    ]);
    return useMemo(() => ({ plugins: [commandPlugin], typeahead }), [commandPlugin, typeahead]);
}
//# sourceMappingURL=useSkillsAndCommandsPlugin.js.map